import React, { useState } from "react";
import {
  Stack,
  Typography,
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Divider,
} from "@mui/material";
import { ShoppingCart, Payment, LocalOffer, Close } from "@mui/icons-material";

interface RecentOrder {
  id?: string;
  timePlaced?: string;
  total?: number | string;
  vendorName?: string;
  orderStatus?: string;
}

interface SavedPaymentMethod {
  id?: string;
  cardType?: string;
  lastFour?: string;
  [key: string]: unknown;
}

interface Customer {
  recentOrdersList?: RecentOrder[];
  redeemablePoints?: number | null;
  savedPaymentMethodsList?: SavedPaymentMethod[] | null;
  rewardOffersList?: (string | { id?: string; name?: string; description?: string; [key: string]: unknown })[];
}

interface PaymentRewardsProps {
  customer: Customer;
}

const CLOSE_BUTTON_SX = {
  color: "#041E42",
  borderColor: "#041E42",
  "&:hover": {
    borderColor: "#0a3d7a",
    backgroundColor: "rgba(4, 30, 66, 0.04)",
  },
};

const formatPayment = (payment: SavedPaymentMethod | null): string => {
  if (!payment) {return "No Saved Payments";}
  if (payment.cardType && payment.lastFour) {
    return `${payment.cardType} ****${payment.lastFour}`;
  }
  return payment.cardType || "Payment Method";
};

const formatOffer = (offer: string | { name?: string; description?: string } | null): string => {
  if (!offer) {return "No Reward Offers";}
  if (typeof offer === "string") {return offer;}
  return offer.name || offer.description || "Offer";
};

export const PaymentRewards = ({ customer }: PaymentRewardsProps) => {
  const [ordersDialogOpen, setOrdersDialogOpen] = useState(false);
  const [paymentsDialogOpen, setPaymentsDialogOpen] = useState(false);
  const [offersDialogOpen, setOffersDialogOpen] = useState(false);

  const recentOrdersList = customer?.recentOrdersList ?? [];
  const savedPaymentMethodsList = customer?.savedPaymentMethodsList ?? [];
  const rewardOffersList = customer?.rewardOffersList ?? [];

  return (
    <>
      <Stack spacing={1}>
        {/* Recent Orders */}
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="body2" fontWeight={600}>Recent Orders:</Typography>
          {recentOrdersList.length > 1 ? (
            <Button
              size="small"
              startIcon={<ShoppingCart sx={{ fontSize: 16 }} />}
              onClick={() => setOrdersDialogOpen(true)}
              sx={{ textTransform: "none", fontSize: "0.8rem", p: 0 }}
            >
              {recentOrdersList.length} orders
            </Button>
          ) : (
            <Typography variant="body2">
              {recentOrdersList.length === 1
                ? (recentOrdersList[0].vendorName || recentOrdersList[0].id || "1 order")
                : "No Recent Orders"}
            </Typography>
          )}
        </Stack>

        {/* Redeemable Points */}
        <Stack direction="row" justifyContent="space-between">
          <Typography variant="body2" fontWeight={600}>Redeemable Points:</Typography>
          <Typography variant="body2">
            {customer?.redeemablePoints ?? "N/A"}
          </Typography>
        </Stack>

        {/* Saved Payments */}
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="body2" fontWeight={600}>Saved Payments:</Typography>
          {savedPaymentMethodsList.length > 1 ? (
            <Button
              size="small"
              startIcon={<Payment sx={{ fontSize: 16 }} />}
              onClick={() => setPaymentsDialogOpen(true)}
              sx={{ textTransform: "none", fontSize: "0.8rem", p: 0 }}
            >
              {savedPaymentMethodsList.length} payments
            </Button>
          ) : (
            <Typography variant="body2">
              {savedPaymentMethodsList.length === 1
                ? formatPayment(savedPaymentMethodsList[0])
                : "No Saved Payments"}
            </Typography>
          )}
        </Stack>

        {/* Reward Offers */}
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="body2" fontWeight={600}>Reward Offers:</Typography>
          {rewardOffersList.length > 1 ? (
            <Button
              size="small"
              startIcon={<LocalOffer sx={{ fontSize: 16 }} />}
              onClick={() => setOffersDialogOpen(true)}
              sx={{ textTransform: "none", fontSize: "0.8rem", p: 0 }}
            >
              {rewardOffersList.length} offers
            </Button>
          ) : (
            <Typography variant="body2">
              {rewardOffersList.length === 1
                ? formatOffer(rewardOffersList[0])
                : "No Reward Offers"}
            </Typography>
          )}
        </Stack>
      </Stack>

      {/* Recent Orders Dialog */}
      <Dialog open={ordersDialogOpen} onClose={() => setOrdersDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          Recent Orders ({recentOrdersList.length})
          <IconButton onClick={() => setOrdersDialogOpen(false)} size="small">
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <List sx={{ p: 0 }}>
            {recentOrdersList.map((order, index) => (
              <React.Fragment key={order.id ?? index}>
                <ListItem sx={{ px: 0 }}>
                  <ListItemText
                    primary={
                      <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                        <Typography variant="body1" fontWeight="bold">
                          {order.vendorName || `Order ${index + 1}`}
                        </Typography>
                        <Typography variant="body1" fontWeight="bold" color="#2e7d32">
                          {order.total ?? "N/A"}
                        </Typography>
                      </Box>
                    }
                    secondary={
                      <Box sx={{ mt: 0.5 }}>
                        {order.orderStatus && (
                          <Typography variant="body2" color="text.secondary">Status: {order.orderStatus}</Typography>
                        )}
                        {order.timePlaced && (
                          <Typography variant="body2" color="text.secondary">Placed: {order.timePlaced}</Typography>
                        )}
                      </Box>
                    }
                  />
                </ListItem>
                {index < recentOrdersList.length - 1 && <Divider />}
              </React.Fragment>
            ))}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOrdersDialogOpen(false)} variant="outlined" sx={CLOSE_BUTTON_SX}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Saved Payments Dialog */}
      <Dialog open={paymentsDialogOpen} onClose={() => setPaymentsDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          Saved Payments ({savedPaymentMethodsList.length})
          <IconButton onClick={() => setPaymentsDialogOpen(false)} size="small">
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <List sx={{ p: 0 }}>
            {savedPaymentMethodsList.map((payment, index) => (
              <React.Fragment key={payment.id ?? `${payment.cardType ?? ""}-${payment.lastFour ?? ""}-${index}`}>
                <ListItem sx={{ px: 0 }}>
                  <ListItemText
                    primary={formatPayment(payment)}
                    secondary={payment.cardType ? `Type: ${payment.cardType}` : undefined}
                  />
                </ListItem>
                {index < savedPaymentMethodsList.length - 1 && <Divider />}
              </React.Fragment>
            ))}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPaymentsDialogOpen(false)} variant="outlined" sx={CLOSE_BUTTON_SX}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Reward Offers Dialog */}
      <Dialog open={offersDialogOpen} onClose={() => setOffersDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          Reward Offers ({rewardOffersList.length})
          <IconButton onClick={() => setOffersDialogOpen(false)} size="small">
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <List sx={{ p: 0 }}>
            {rewardOffersList.map((offer, offerIdx) => (
              <React.Fragment key={typeof offer === "string" ? `${offer}-${offerIdx}` : (offer.id ?? `offer-${offerIdx}`)}>
                <ListItem sx={{ px: 0 }}>
                  <ListItemText primary={formatOffer(offer)} />
                </ListItem>
                {offerIdx < rewardOffersList.length - 1 && <Divider />}
              </React.Fragment>
            ))}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOffersDialogOpen(false)} variant="outlined" sx={CLOSE_BUTTON_SX}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
